import { useState, useEffect, useCallback, useRef } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import {
  Package,
  Plus,
  Search,
  Trash2,
  Edit3,
  AlertTriangle,
  Loader2,
  X,
  Download,
  Upload,
  History,
  TrendingUp,
  TrendingDown,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle,
  FileSpreadsheet,
  Box,
  Link2,
  ShoppingBag,
  RefreshCw,
} from 'lucide-react';
import {
  warehouseApi,
  storeApi,
  type Store,
  type Product,
  type ParentProduct,
  type ProductFilters,
  type CreateProductRequest,
  type UpdateProductRequest,
  type StockMovement,
  type ImportProductData,
  type WarehouseReport,
  type WarehouseAlert,
} from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

export function WarehousePage() {
  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [stats, setStats] = useState({
    totalProducts: 0,
    lowStockCount: 0,
  });
  const [stores, setStores] = useState<Store[]>([]);
  const [filters, setFilters] = useState<ProductFilters>(() => ({
    page: 1,
    limit: 20,
    search: '',
    isActive: 'all',
    lowStock: undefined,
    storeId: localStorage.getItem('warehouse_storeFilter') || '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  }));
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [adjustingProduct, setAdjustingProduct] = useState<Product | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState<Product | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchDebounce, setSearchDebounce] = useState('');
  const [activeTab, setActiveTab] = useState<'products' | 'reports' | 'alerts'>('products');
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [report, setReport] = useState<WarehouseReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [alerts, setAlerts] = useState<WarehouseAlert[]>([]);
  const [alertCounts, setAlertCounts] = useState({ total: 0, critical: 0, warning: 0 });
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  // Load stores for filter dropdown
  useEffect(() => {
    storeApi.getAll().then(({ data }) => setStores(data)).catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchDebounce, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchDebounce]);

  // Request counter to prevent race conditions - stale responses are ignored
  const requestCounterRef = useRef(0);
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  // Reload trigger for external callers (handleDelete, modals, etc.)
  const [reloadKey, setReloadKey] = useState(0);
  const reloadProducts = useCallback(() => setReloadKey(k => k + 1), []);

  useEffect(() => {
    const currentRequestId = ++requestCounterRef.current;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        // Clean up params - don't send empty/undefined values
        const params: ProductFilters = {
          page: filters.page,
          limit: filters.limit,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
        };
        if (filters.search) params.search = filters.search;
        if (filters.isActive && filters.isActive !== 'all') params.isActive = filters.isActive;
        if (filters.lowStock) params.lowStock = filters.lowStock;
        if (filters.storeId) params.storeId = filters.storeId;

        const { data } = await warehouseApi.getProducts(params);

        // Only update state if this is still the latest request
        if (currentRequestId === requestCounterRef.current) {
          setProducts(data.products);
          setPagination(data.pagination);
          setStats(data.stats);
        }
      } catch (err) {
        if (currentRequestId === requestCounterRef.current) {
          showErrorRef.current(err, 'warehouse');
        }
      } finally {
        if (currentRequestId === requestCounterRef.current) {
          setLoading(false);
        }
      }
    };

    fetchProducts();
  }, [filters, reloadKey]);

  const loadReport = useCallback(async () => {
    try {
      setLoadingReport(true);
      const reportParams: { storeId?: string } = {};
      if (filters.storeId) reportParams.storeId = filters.storeId;
      const { data } = await warehouseApi.getReport('overview', reportParams);
      setReport(data);
    } catch (err) {
      showError(err, 'warehouse');
    } finally {
      setLoadingReport(false);
    }
  }, [showError, filters.storeId]);

  const loadAlerts = useCallback(async () => {
    try {
      setLoadingAlerts(true);
      const alertParams: { storeId?: string } = {};
      if (filters.storeId) alertParams.storeId = filters.storeId;
      const { data } = await warehouseApi.getAlerts(alertParams);
      setAlerts(data.alerts);
      setAlertCounts({ total: data.totalAlerts, critical: data.criticalCount, warning: data.warningCount });
    } catch (err) {
      showError(err, 'warehouse');
    } finally {
      setLoadingAlerts(false);
    }
  }, [showError, filters.storeId]);

  // Load reports/alerts when tab changes or storeId filter changes
  useEffect(() => {
    if (activeTab === 'reports') loadReport();
    if (activeTab === 'alerts') loadAlerts();
  }, [activeTab, loadReport, loadAlerts]);

  const handleDelete = async (product: Product) => {
    if (!(await confirmDialog({
      title: 'Usuń produkt',
      message: <>Czy na pewno chcesz usunąć produkt <strong>{product.name}</strong>?</>,
      confirmLabel: 'Usuń',
    }))) return;
    try {
      await warehouseApi.deleteProduct(product.id);
      showSuccess('warehouse', { message: 'Produkt został usunięty' });
      reloadProducts();
    } catch (err) {
      showError(err, 'warehouse');
    }
  };

  const handleExport = async () => {
    try {
      const response = await warehouseApi.exportProducts();
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `produkty_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSuccess('warehouse', { message: 'Eksport zakończony' });
    } catch (err) {
      showError(err, 'warehouse');
    }
  };

  const handleSyncFromWooCommerce = async () => {
    setSyncingProducts(true);
    try {
      const selectedStoreId = filters.storeId && filters.storeId !== 'none' ? filters.storeId : undefined;
      const { data } = await warehouseApi.syncProductsFromWooCommerce(selectedStoreId);
      showSuccess('warehouse', {
        message: data.message || `Synchronizacja zakończona. Utworzono: ${data.created}, Zaktualizowano: ${data.updated}`,
      });
      // Reload product list after sync
      reloadProducts();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosErr.response?.status === 429) {
        showError(new Error(axiosErr.response.data?.message || 'Zbyt częste synchronizacje. Spróbuj ponownie za kilka minut.'), 'warehouse');
      } else {
        showError(err, 'warehouse');
      }
    } finally {
      setSyncingProducts(false);
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return '-';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(price);
  };

  const isLowStock = (product: Product) => {
    if (!(product.parentProduct?.trackStock ?? product.trackStock)) return false;
    const effectiveQuantity = product.parentProduct?.quantity ?? product.quantity;
    return product.minQuantity !== null && effectiveQuantity <= product.minQuantity;
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Magazyn</h1>
            <p className="text-text-secondary mt-2">
              Zarządzaj produktami i stanami magazynowymi
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Store selector - global for all tabs */}
            <div className="relative">
              <ShoppingBag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
              <select
                value={filters.storeId || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  localStorage.setItem('warehouse_storeFilter', val);
                  setFilters(f => ({ ...f, storeId: val, page: 1 }));
                }}
                className="input pl-10 pr-8 w-auto min-w-[180px]"
              >
                <option value="">Wszystkie sklepy</option>
                <option value="none">Bez sklepu</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleSyncFromWooCommerce}
              disabled={syncingProducts}
              className="btn-secondary"
              title="Pobierz produkty z WooCommerce (automatycznie co 24h)"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncingProducts ? 'animate-spin' : ''}`} />
              {syncingProducts ? 'Synchronizacja...' : 'Sync WooCommerce'}
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-secondary"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import
            </button>
            <button onClick={handleExport} className="btn-secondary">
              <Download className="w-4 h-4 mr-2" />
              Eksport
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              <Plus className="w-5 h-5 mr-2" />
              Dodaj produkt
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-surface-hover/50 rounded-xl p-1 animate-slide-up">
          <button
            onClick={() => setActiveTab('products')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'products'
                ? 'bg-surface-muted text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Package className="w-4 h-4" />
            Produkty
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'reports'
                ? 'bg-surface-muted text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Raporty
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'alerts'
                ? 'bg-surface-muted text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            Alerty
            {stats.lowStockCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-400">
                {stats.lowStockCount}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'products' && (
        <>
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-slide-up">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent-primary/10">
                <Package className="w-5 h-5 text-accent-primary" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Produkty</p>
                <p className="text-xl font-semibold text-text-primary">
                  {stats.totalProducts}
                </p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent-secondary/10">
                <Box className="w-5 h-5 text-accent-secondary" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Na stronie</p>
                <p className="text-xl font-semibold text-text-primary">
                  {products.length}
                </p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stats.lowStockCount > 0 ? 'bg-amber-500/10' : 'bg-success/10'}`}>
                <AlertTriangle className={`w-5 h-5 ${stats.lowStockCount > 0 ? 'text-amber-400' : 'text-success'}`} />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Niski stan</p>
                <p className={`text-xl font-semibold ${stats.lowStockCount > 0 ? 'text-amber-400' : 'text-text-primary'}`}>
                  {stats.lowStockCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card p-4 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Szukaj produktu..."
                value={searchDebounce}
                onChange={(e) => setSearchDebounce(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>

            {/* Active filter */}
            <select
              value={filters.isActive}
              onChange={(e) => setFilters(f => ({ ...f, isActive: e.target.value as 'all' | 'true' | 'false', page: 1 }))}
              className="input w-full sm:w-auto"
            >
              <option value="all">Wszystkie</option>
              <option value="true">Aktywne</option>
              <option value="false">Nieaktywne</option>
            </select>

            {/* Low stock filter */}
            <button
              onClick={() => setFilters(f => ({
                ...f,
                lowStock: f.lowStock === 'true' ? undefined : 'true',
                page: 1
              }))}
              className={`btn-secondary whitespace-nowrap ${filters.lowStock === 'true' ? 'ring-2 ring-amber-400' : ''}`}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Niski stan
            </button>

            {/* Sort */}
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-') as [ProductFilters['sortBy'], 'asc' | 'desc'];
                setFilters(f => ({ ...f, sortBy, sortOrder, page: 1 }));
              }}
              className="input w-full sm:w-auto"
            >
              <option value="createdAt-desc">Najnowsze</option>
              <option value="createdAt-asc">Najstarsze</option>
              <option value="name-asc">Nazwa A-Z</option>
              <option value="name-desc">Nazwa Z-A</option>
              <option value="quantity-asc">Stan rosnąco</option>
              <option value="quantity-desc">Stan malejąco</option>
              <option value="sellingPrice-asc">Cena rosnąco</option>
              <option value="sellingPrice-desc">Cena malejąco</option>
            </select>
          </div>
        </div>

        {/* Products list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="loader" />
          </div>
        ) : products.length === 0 ? (
          <div className="card animate-slide-up">
            <EmptyState
              icon={Package}
              title={filters.search || filters.lowStock ? 'Brak wyników' : 'Brak produktów'}
              description={filters.search || filters.lowStock ? 'Zmień kryteria wyszukiwania' : 'Dodaj pierwszy produkt do magazynu'}
              {...(!filters.search && !filters.lowStock ? { buttonLabel: 'Dodaj produkt', onButtonClick: () => setShowAddModal(true), buttonIcon: Plus } : {})}
            />
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="card overflow-hidden animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-subtle">
                      <th className="text-left text-xs font-medium text-text-secondary uppercase tracking-wider p-4">
                        Produkt
                      </th>
                      <th className="text-left text-xs font-medium text-text-secondary uppercase tracking-wider p-4">
                        SKU
                      </th>
                      <th className="text-right text-xs font-medium text-text-secondary uppercase tracking-wider p-4">
                        Cena zakupu
                      </th>
                      <th className="text-right text-xs font-medium text-text-secondary uppercase tracking-wider p-4">
                        Cena sprzedaży
                      </th>
                      <th className="text-center text-xs font-medium text-text-secondary uppercase tracking-wider p-4">
                        Stan
                      </th>
                      <th className="text-center text-xs font-medium text-text-secondary uppercase tracking-wider p-4">
                        Status
                      </th>
                      <th className="text-center text-xs font-medium text-text-secondary uppercase tracking-wider p-4">
                        Sync WC
                      </th>
                      <th className="text-right text-xs font-medium text-text-secondary uppercase tracking-wider p-4">
                        Akcje
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {products.map((product) => (
                      <tr
                        key={product.id}
                        className="hover:bg-surface-hover/30 transition-colors"
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${product.isActive ? 'bg-accent-primary/10' : 'bg-surface-hover'}`}>
                              <Package className={`w-4 h-4 ${product.isActive ? 'text-accent-primary' : 'text-text-muted'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-text-primary">
                                  {product.name}
                                  {product.woocommerceProductId && (
                                    <span className="text-xs text-text-muted font-normal ml-1.5">(WC#{product.woocommerceProductId})</span>
                                  )}
                                </p>
                                {product.parentProductId && product.parentProduct && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-accent-secondary/15 text-accent-secondary text-[10px] font-medium">
                                    <Link2 className="w-3 h-3" />
                                    {product.parentProduct.name}
                                    {product.parentProduct.sku ? ` (${product.parentProduct.sku})` : ''}
                                  </span>
                                )}
                                {product.childProducts && product.childProducts.length > 0 && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-accent-primary/15 text-accent-primary text-[10px] font-medium">
                                    matka ({product.childProducts.length})
                                  </span>
                                )}
                              </div>
                              {product.description && (
                                <p className="text-xs text-text-muted truncate max-w-[200px]">
                                  {product.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-text-secondary">
                            {product.sku || '-'}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-sm text-text-secondary">
                            {formatPrice(product.purchasePrice)}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-sm text-text-primary font-medium">
                            {formatPrice(product.sellingPrice)}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {(product.parentProduct?.trackStock ?? product.trackStock) === false ? (
                              <span className="text-xs text-text-muted italic">bez kontroli</span>
                            ) : product.parentProductId && product.parentProduct ? (
                              <>
                                <Link2 className="w-3 h-3 text-accent-secondary" />
                                <span className="text-sm font-semibold text-accent-secondary">
                                  {product.parentProduct.quantity}
                                </span>
                                <span className="text-[10px] text-text-muted">
                                  (matka)
                                </span>
                              </>
                            ) : (
                              <>
                                <span className={`text-sm font-semibold ${
                                  isLowStock(product) ? 'text-amber-400' : 'text-text-primary'
                                }`}>
                                  {product.quantity}
                                </span>
                                {isLowStock(product) && (
                                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                                )}
                                {product.minQuantity !== null && (
                                  <span className="text-xs text-text-muted">
                                    (min: {product.minQuantity})
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <span className={`badge ${product.isActive ? 'badge-success' : 'badge-error'}`}>
                            {product.isActive ? 'Aktywny' : 'Nieaktywny'}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          {product.woocommerceProductId ? (
                            <span className={`badge ${
                              product.syncStatus === 'SYNCED' ? 'badge-success' :
                              product.syncStatus === 'PENDING' ? 'badge-warning' :
                              product.syncStatus === 'ERROR' ? 'badge-error' :
                              'badge-info'
                            }`}>
                              {product.syncStatus === 'SYNCED' ? 'Zsync.' :
                               product.syncStatus === 'PENDING' ? 'Oczekuje' :
                               product.syncStatus === 'ERROR' ? 'Błąd' :
                               'Nie zsync.'}
                            </span>
                          ) : (
                            <span className="text-xs text-text-muted">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setAdjustingProduct(product)}
                              className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-success hover:bg-surface-hover transition-all"
                              title="Korekta stanu"
                            >
                              <TrendingUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setShowHistoryModal(product)}
                              className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-accent-secondary hover:bg-surface-hover transition-all"
                              title="Historia"
                            >
                              <History className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingProduct(product)}
                              className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-accent-primary hover:bg-surface-hover transition-all"
                              title="Edytuj"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(product)}
                              className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-danger hover:bg-danger/10 transition-all"
                              title="Usuń"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-text-secondary">
                  Pokazuję {(pagination.page - 1) * pagination.limit + 1} -{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} z{' '}
                  {pagination.total} produktów
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFilters(f => ({ ...f, page: f.page! - 1 }))}
                    disabled={pagination.page <= 1}
                    className="btn-secondary p-2 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-text-secondary px-3">
                    {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setFilters(f => ({ ...f, page: f.page! + 1 }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="btn-secondary p-2 disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        </>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6 animate-fade-in">
            {loadingReport ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
              </div>
            ) : report && (
              <>
                {/* Overview Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="card p-4">
                    <p className="text-xs text-text-secondary">Produkty</p>
                    <p className="text-2xl font-bold text-text-primary">{report.totalProducts}</p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-text-secondary">Sztuk na stanie</p>
                    <p className="text-2xl font-bold text-text-primary">{report.totalItems}</p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-text-secondary">Niski stan</p>
                    <p className={`text-2xl font-bold ${report.lowStockCount > 0 ? 'text-amber-400' : 'text-success'}`}>
                      {report.lowStockCount}
                    </p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-text-secondary">Brak na stanie</p>
                    <p className={`text-2xl font-bold ${report.outOfStockCount > 0 ? 'text-danger' : 'text-success'}`}>
                      {report.outOfStockCount}
                    </p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-text-secondary">Wartość zakupu</p>
                    <p className="text-2xl font-bold text-text-primary">
                      {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(report.totalStockValue)}
                    </p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-text-secondary">Wartość sprzedaży</p>
                    <p className="text-2xl font-bold text-accent-primary">
                      {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(report.totalSellingValue)}
                    </p>
                  </div>
                </div>

                {/* Movements Summary (last 30 days) */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                    <History className="w-5 h-5 text-accent-primary" />
                    Ruchy magazynowe (ostatnie 30 dni)
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-4 bg-surface-hover/30 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingDown className="w-4 h-4 text-danger" />
                        <span className="text-sm text-text-secondary">Wydania</span>
                      </div>
                      <p className="text-xl font-bold text-danger">{report.movementsLast30Days.decreases} szt.</p>
                    </div>
                    <div className="p-4 bg-surface-hover/30 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-success" />
                        <span className="text-sm text-text-secondary">Przyjęcia</span>
                      </div>
                      <p className="text-xl font-bold text-success">{report.movementsLast30Days.increases} szt.</p>
                    </div>
                    <div className="p-4 bg-surface-hover/30 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <History className="w-4 h-4 text-accent-primary" />
                        <span className="text-sm text-text-secondary">Korekty</span>
                      </div>
                      <p className="text-xl font-bold text-accent-primary">{report.movementsLast30Days.adjustments} szt.</p>
                    </div>
                  </div>
                </div>

                {/* Low Stock Products */}
                {report.lowStockProducts.length > 0 && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                      Produkty z niskim stanem ({report.lowStockProducts.length})
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-text-secondary border-b border-border-subtle">
                            <th className="text-left py-2 px-3">Produkt</th>
                            <th className="text-left py-2 px-3">SKU</th>
                            <th className="text-center py-2 px-3">Stan</th>
                            <th className="text-center py-2 px-3">Minimum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.lowStockProducts.map(p => (
                            <tr key={p.id} className="border-b border-border-subtle hover:bg-surface-hover/30">
                              <td className="py-2 px-3 text-text-primary">{p.name}</td>
                              <td className="py-2 px-3 text-text-secondary">{p.sku || '-'}</td>
                              <td className={`py-2 px-3 text-center font-medium ${p.quantity === 0 ? 'text-danger' : 'text-amber-400'}`}>
                                {p.quantity}
                              </td>
                              <td className="py-2 px-3 text-center text-text-secondary">{p.minQuantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Out of Stock Products */}
                {report.outOfStockProducts.length > 0 && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-danger" />
                      Produkty bez stanu ({report.outOfStockProducts.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {report.outOfStockProducts.map(p => (
                        <span key={p.id} className="px-3 py-1.5 rounded-lg bg-danger/10 text-danger text-sm">
                          {p.name} {p.sku && <span className="text-danger/60">({p.sku})</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div className="space-y-6 animate-fade-in">
            {loadingAlerts ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
              </div>
            ) : (
              <>
                {/* Alert Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="card p-4">
                    <p className="text-xs text-text-secondary">Wszystkie alerty</p>
                    <p className={`text-2xl font-bold ${alertCounts.total > 0 ? 'text-amber-400' : 'text-success'}`}>
                      {alertCounts.total}
                    </p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-text-secondary">Krytyczne (brak stanu)</p>
                    <p className={`text-2xl font-bold ${alertCounts.critical > 0 ? 'text-danger' : 'text-success'}`}>
                      {alertCounts.critical}
                    </p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-text-secondary">Ostrzeżenia (niski stan)</p>
                    <p className={`text-2xl font-bold ${alertCounts.warning > 0 ? 'text-amber-400' : 'text-success'}`}>
                      {alertCounts.warning}
                    </p>
                  </div>
                </div>

                {/* Alert List */}
                {alerts.length === 0 ? (
                  <div className="card">
                    <EmptyState
                      icon={Check}
                      title="Brak alertów"
                      description="Wszystkie produkty mają wystarczający stan magazynowy"
                      size="sm"
                      iconColorClass="from-success/20 to-success/10 border-success/20 text-success"
                    />
                  </div>
                ) : (
                  <div className="card">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-text-secondary border-b border-border-subtle">
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Produkt</th>
                            <th className="text-left py-3 px-4">SKU</th>
                            <th className="text-center py-3 px-4">Stan</th>
                            <th className="text-center py-3 px-4">Minimum</th>
                            <th className="text-center py-3 px-4">Brakuje</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alerts.map(alert => (
                            <tr key={alert.id} className="border-b border-border-subtle hover:bg-surface-hover/30">
                              <td className="py-3 px-4">
                                {alert.severity === 'critical' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-danger/15 text-danger text-xs font-medium">
                                    <AlertCircle className="w-3 h-3" />
                                    Krytyczny
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-xs font-medium">
                                    <AlertTriangle className="w-3 h-3" />
                                    Ostrzeżenie
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-text-primary font-medium">{alert.name}</td>
                              <td className="py-3 px-4 text-text-secondary">{alert.sku || '-'}</td>
                              <td className={`py-3 px-4 text-center font-bold ${alert.quantity === 0 ? 'text-danger' : 'text-amber-400'}`}>
                                {alert.quantity}
                              </td>
                              <td className="py-3 px-4 text-center text-text-secondary">{alert.minQuantity}</td>
                              <td className="py-3 px-4 text-center text-danger font-medium">
                                {(alert.minQuantity || 0) - alert.quantity}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <ProductModal
          defaultStoreId={filters.storeId && filters.storeId !== 'none' ? filters.storeId : null}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            reloadProducts();
          }}
        />
      )}

      {editingProduct && (
        <ProductModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSuccess={() => {
            setEditingProduct(null);
            reloadProducts();
          }}
        />
      )}

      {adjustingProduct && (
        <AdjustStockModal
          product={adjustingProduct}
          onClose={() => setAdjustingProduct(null)}
          onSuccess={() => {
            setAdjustingProduct(null);
            reloadProducts();
          }}
        />
      )}

      {showHistoryModal && (
        <StockHistoryModal
          product={showHistoryModal}
          onClose={() => setShowHistoryModal(null)}
        />
      )}

      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            reloadProducts();
          }}
        />
      )}
    </>
  );
}

// ============ PRODUCT MODAL ============

function ProductModal({
  product,
  defaultStoreId,
  onClose,
  onSuccess,
}: {
  product?: Product;
  defaultStoreId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [parentProducts, setParentProducts] = useState<ParentProduct[]>([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [formData, setFormData] = useState<CreateProductRequest>({
    name: product?.name || '',
    sku: product?.sku || '',
    description: product?.description || '',
    purchasePrice: product?.purchasePrice ?? null,
    sellingPrice: product?.sellingPrice ?? null,
    quantity: product?.quantity ?? 0,
    minQuantity: product?.minQuantity ?? null,
    trackStock: product?.trackStock ?? true,
    storeId: product?.storeId ?? defaultStoreId ?? null,
    woocommerceProductId: product?.woocommerceProductId ?? null,
    isActive: product?.isActive ?? true,
    parentProductId: product?.parentProductId ?? null,
  });

  const isParent = product?.childProducts && product.childProducts.length > 0;

  // Load available parent products
  useEffect(() => {
    const loadParents = async () => {
      try {
        setLoadingParents(true);
        const parentStoreId = (product ? product.storeId : defaultStoreId) ?? null;
        const { data } = await warehouseApi.getParentProducts(product?.id, parentStoreId);
        setParentProducts(data);
      } catch {
        // Silent fail - dropdown will just be empty
      } finally {
        setLoadingParents(false);
      }
    };
    loadParents();
  }, [defaultStoreId, product?.id, product?.storeId]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (product) {
        const updateData: UpdateProductRequest = {
          name: formData.name,
          sku: formData.sku || null,
          description: formData.description || null,
          purchasePrice: formData.purchasePrice,
          sellingPrice: formData.sellingPrice,
          minQuantity: formData.minQuantity,
          trackStock: formData.trackStock,
          woocommerceProductId: formData.woocommerceProductId || null,
          isActive: formData.isActive,
          parentProductId: formData.parentProductId || null,
        };
        await warehouseApi.updateProduct(product.id, updateData);
        showSuccess('warehouse', { message: 'Produkt został zaktualizowany' });
      } else {
        await warehouseApi.createProduct(formData);
        showSuccess('warehouse', { message: 'Produkt został dodany' });
      }
      onSuccess();
    } catch (err) {
      showError(err, 'warehouse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-surface-raised rounded-2xl shadow-2xl border border-border animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-text-primary">
            {product ? 'Edytuj produkt' : 'Dodaj produkt'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-hover/50 transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Nazwa <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
              className="input w-full"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                SKU
              </label>
              <input
                type="text"
                value={formData.sku || ''}
                onChange={(e) => setFormData(f => ({ ...f, sku: e.target.value }))}
                className="input w-full"
                placeholder="np. PROD-001"
              />
            </div>
            {!product && !formData.parentProductId && (
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Stan początkowy
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData(f => ({ ...f, quantity: parseInt(e.target.value) || 0 }))}
                  className="input w-full"
                />
              </div>
            )}
            {product && (
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Min. ilość (alert)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.minQuantity ?? ''}
                  onChange={(e) => setFormData(f => ({ ...f, minQuantity: e.target.value ? parseInt(e.target.value) : null }))}
                  className="input w-full"
                  placeholder="np. 5"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Cena zakupu
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.purchasePrice ?? ''}
                onChange={(e) => setFormData(f => ({ ...f, purchasePrice: e.target.value ? parseFloat(e.target.value) : null }))}
                className="input w-full"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Cena sprzedaży
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.sellingPrice ?? ''}
                onChange={(e) => setFormData(f => ({ ...f, sellingPrice: e.target.value ? parseFloat(e.target.value) : null }))}
                className="input w-full"
                placeholder="0.00"
              />
            </div>
          </div>

          {!product && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Min. ilość (alert)
              </label>
              <input
                type="number"
                min="0"
                value={formData.minQuantity ?? ''}
                onChange={(e) => setFormData(f => ({ ...f, minQuantity: e.target.value ? parseInt(e.target.value) : null }))}
                className="input w-full"
                placeholder="np. 5"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Opis
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
              className="input w-full h-24 resize-none"
              placeholder="Opcjonalny opis produktu..."
            />
          </div>

          {/* Parent product selector */}
          {!isParent && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                <span className="flex items-center gap-1.5">
                  <Link2 className="w-3.5 h-3.5 text-accent-secondary" />
                  Produkt-matka (współdzielony stan magazynowy)
                </span>
              </label>
              <select
                value={formData.parentProductId || ''}
                onChange={(e) => setFormData(f => ({ ...f, parentProductId: e.target.value || null }))}
                className="input w-full"
                disabled={loadingParents}
              >
                <option value="">Brak — własny stan magazynowy</option>
                {parentProducts.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.sku ? `(${p.sku})` : ''}{p.woocommerceProductId ? ` [WC#${p.woocommerceProductId}]` : ''} — stan: {p.quantity}
                    {p.childProducts.length > 0 ? ` [+${p.childProducts.length} podpiętych]` : ''}
                  </option>
                ))}
              </select>
              {formData.parentProductId && (
                <p className="text-xs text-accent-secondary/70 mt-1">
                  Stan magazynowy tego produktu będzie pobierany z produktu-matki. Zamówienia pomniejszą stan matki.
                </p>
              )}
            </div>
          )}

          {isParent && (
            <div className="p-3 rounded-lg bg-accent-primary/5 border border-accent-primary/10">
              <p className="text-sm text-accent-primary flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Ten produkt jest matką dla {product!.childProducts!.length} produktów-dzieci.
              </p>
              <p className="text-xs text-text-muted mt-1">
                Nie można podpiąć produktu-matki pod inny produkt, dopóki ma produkty-dzieci.
              </p>
            </div>
          )}

          {/* WooCommerce Product ID - read-only badge when synced, collapsible when not */}
          {product && product.syncStatus === 'SYNCED' && product.woocommerceProductId ? (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-surface-hover/30">
              <span className="text-sm text-text-secondary">WooCommerce:</span>
              <span className="px-2 py-0.5 rounded-md bg-accent-primary/15 text-accent-primary text-sm font-mono">
                WC#{product.woocommerceProductId}
              </span>
              <span className="badge badge-success text-xs">Zsynchronizowany</span>
            </div>
          ) : (
            <details className="group">
              <summary className="text-sm text-text-secondary cursor-pointer hover:text-text-secondary transition-colors">
                Zaawansowane
              </summary>
              <div className="mt-2">
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  WooCommerce Product ID
                </label>
                <input
                  type="text"
                  value={formData.woocommerceProductId || ''}
                  onChange={(e) => setFormData(f => ({ ...f, woocommerceProductId: e.target.value || null }))}
                  className="input w-full"
                  placeholder="np. 123 (ID produktu w WooCommerce)"
                />
                <p className="text-xs text-text-muted mt-1">
                  Powiąż produkt z WooCommerce, aby umożliwić synchronizację stanów
                </p>
              </div>
            </details>
          )}

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => setFormData(f => ({ ...f, trackStock: !f.trackStock }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  formData.trackStock ? 'bg-accent-primary' : 'bg-surface-hover'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    formData.trackStock ? 'left-7' : 'left-1'
                  }`}
                />
              </div>
              <div>
                <span className="text-sm text-text-secondary">Kontrola stanów magazynowych</span>
                <p className="text-xs text-text-muted">Wyłącz dla usług, e-booków i produktów o nieograniczonej dostępności</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => setFormData(f => ({ ...f, isActive: !f.isActive }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  formData.isActive ? 'bg-success' : 'bg-surface-hover'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    formData.isActive ? 'left-7' : 'left-1'
                  }`}
                />
              </div>
              <span className="text-sm text-text-secondary">
                Produkt aktywny
              </span>
            </label>
          </div>

          <div className="flex justify-between gap-3 pt-4">
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {product ? 'Zapisz' : 'Dodaj'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">
              Anuluj
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ ADJUST STOCK MODAL ============

function AdjustStockModal({
  product,
  onClose,
  onSuccess,
}: {
  product: Product;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState<'increase' | 'decrease'>('increase');
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const currentQuantity = product.parentProduct?.quantity ?? product.quantity;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const adjustQuantity = adjustmentType === 'decrease' ? -quantity : quantity;
      await warehouseApi.adjustStock(product.id, {
        quantity: adjustQuantity,
        reason: reason || undefined,
        notes: notes || undefined,
      });
      showSuccess('warehouse', {
        message: `Stan magazynowy ${adjustmentType === 'increase' ? 'zwiększony' : 'zmniejszony'} o ${quantity}`
      });
      onSuccess();
    } catch (err) {
      showError(err, 'warehouse');
    } finally {
      setLoading(false);
    }
  };

  const newQuantity = adjustmentType === 'decrease'
    ? currentQuantity - quantity
    : currentQuantity + quantity;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-surface-raised rounded-2xl shadow-2xl border border-border animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-text-primary">Korekta stanu</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-hover/50 transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="p-4 rounded-lg bg-surface-hover/50">
            <p className="text-sm text-text-secondary">Produkt</p>
            <p className="text-text-primary font-medium">{product.name}</p>
            <p className="text-sm text-text-muted mt-1">
              Aktualny stan: <span className="text-accent-primary font-semibold">{currentQuantity}</span>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setAdjustmentType('increase')}
              className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                adjustmentType === 'increase'
                  ? 'border-success bg-success/10 text-success'
                  : 'border-border text-text-secondary hover:border-border-strong'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              Zwiększ
            </button>
            <button
              type="button"
              onClick={() => setAdjustmentType('decrease')}
              className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                adjustmentType === 'decrease'
                  ? 'border-red-400 bg-danger/10 text-danger'
                  : 'border-border text-text-secondary hover:border-border-strong'
              }`}
            >
              <TrendingDown className="w-4 h-4" />
              Zmniejsz
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Ilość
            </label>
            <input
              type="number"
              min="1"
              max={adjustmentType === 'decrease' ? currentQuantity : undefined}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              className="input w-full"
            />
          </div>

          <div className="p-3 rounded-lg bg-surface-hover/30 flex items-center justify-between">
            <span className="text-sm text-text-secondary">Nowy stan:</span>
            <span className={`font-semibold ${newQuantity < 0 ? 'text-danger' : 'text-accent-primary'}`}>
              {newQuantity}
            </span>
          </div>

          {newQuantity < 0 && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 text-danger text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Stan nie może być ujemny
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Powód
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input w-full"
              placeholder="np. Przyjęcie towaru, Inwentaryzacja..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Notatki
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input w-full h-20 resize-none"
              placeholder="Opcjonalne dodatkowe informacje..."
            />
          </div>

          <div className="flex justify-between gap-3 pt-4">
            <button
              type="submit"
              disabled={loading || newQuantity < 0}
              className="btn-primary"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Zapisz korektę
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">
              Anuluj
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ STOCK HISTORY MODAL ============

function StockHistoryModal({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadMovements();
  }, [page]);

  const loadMovements = async () => {
    try {
      setLoading(true);
      const { data } = await warehouseApi.getStockMovements(product.id, { page, limit: 10 });
      setMovements(data.movements);
      setTotalPages(data.pagination.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getMovementIcon = (type: StockMovement['type']) => {
    switch (type) {
      case 'INCREASE':
        return <TrendingUp className="w-4 h-4 text-success" />;
      case 'DECREASE':
        return <TrendingDown className="w-4 h-4 text-danger" />;
      default:
        return <History className="w-4 h-4 text-text-secondary" />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-surface-raised rounded-2xl shadow-2xl border border-border animate-scale-in max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Historia stanów</h2>
            <p className="text-sm text-text-secondary mt-1">{product.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-hover/50 transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="loader" />
            </div>
          ) : movements.length === 0 ? (
            <EmptyState
              icon={History}
              title="Brak historii ruchów magazynowych"
              size="sm"
            />
          ) : (
            <div className="space-y-3">
              {movements.map((movement) => (
                <div
                  key={movement.id}
                  className="p-4 rounded-lg bg-surface-hover/50 flex items-start gap-4"
                >
                  <div className="p-2 rounded-lg bg-surface-raised">
                    {getMovementIcon(movement.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${
                        movement.quantity > 0 ? 'text-success' : 'text-danger'
                      }`}>
                        {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                      </span>
                      <span className="text-xs text-text-muted">
                        {new Date(movement.createdAt).toLocaleString('pl-PL')}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary mt-1">
                      {movement.reason || movement.type}
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      Stan: {movement.previousQuantity} → {movement.newQuantity}
                    </p>
                    {movement.notes && (
                      <p className="text-xs text-text-muted mt-1 italic">
                        {movement.notes}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="p-4 border-t border-border-subtle flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page <= 1}
              className="btn-secondary p-2 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-text-secondary px-3">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages}
              className="btn-secondary p-2 disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ IMPORT MODAL ============

function ImportModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [previewData, setPreviewData] = useState<ImportProductData[]>([]);
  const [parseError, setParseError] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError('');

    try {
      const text = await selectedFile.text();
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        setParseError('Plik musi zawierać nagłówek i przynajmniej jeden wiersz danych');
        return;
      }

      // Parse CSV (handle both ; and , as delimiter)
      const delimiter = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].split(delimiter).map(h => h.replace(/"/g, '').trim().toLowerCase());

      // Map headers to our fields
      const fieldMap: Record<string, keyof ImportProductData> = {
        'nazwa': 'name',
        'name': 'name',
        'sku': 'sku',
        'opis': 'description',
        'description': 'description',
        'cena zakupu': 'purchasePrice',
        'purchase price': 'purchasePrice',
        'purchaseprice': 'purchasePrice',
        'cena sprzedaży': 'sellingPrice',
        'cena sprzedazy': 'sellingPrice',
        'selling price': 'sellingPrice',
        'sellingprice': 'sellingPrice',
        'ilość': 'quantity',
        'ilosc': 'quantity',
        'quantity': 'quantity',
        'min. ilość': 'minQuantity',
        'min. ilosc': 'minQuantity',
        'min quantity': 'minQuantity',
        'minquantity': 'minQuantity',
      };

      const products: ImportProductData[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i], delimiter);
        const product: ImportProductData = { name: '' };

        headers.forEach((header, idx) => {
          const field = fieldMap[header];
          if (field && values[idx] !== undefined) {
            const value = values[idx].replace(/"/g, '').trim();
            if (field === 'name') {
              product.name = value || '';
            } else if (field === 'sku') {
              product.sku = value || null;
            } else if (field === 'description') {
              product.description = value || null;
            } else if (field === 'purchasePrice') {
              product.purchasePrice = value ? parseFloat(value.replace(',', '.')) : null;
            } else if (field === 'sellingPrice') {
              product.sellingPrice = value ? parseFloat(value.replace(',', '.')) : null;
            } else if (field === 'quantity') {
              product.quantity = value ? parseInt(value) : 0;
            } else if (field === 'minQuantity') {
              product.minQuantity = value ? parseInt(value) : null;
            }
          }
        });

        if (product.name) {
          products.push(product);
        }
      }

      if (products.length === 0) {
        setParseError('Nie znaleziono prawidłowych produktów w pliku');
        return;
      }

      setPreviewData(products);
    } catch (err) {
      setParseError('Błąd parsowania pliku CSV');
    }
  };

  const parseCSVLine = (line: string, delimiter: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleImport = async () => {
    if (previewData.length === 0) return;

    setLoading(true);
    try {
      const { data } = await warehouseApi.importProducts(previewData, updateExisting);
      showSuccess('warehouse', { message: data.message });
      onSuccess();
    } catch (err) {
      showError(err, 'warehouse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-surface-raised rounded-2xl shadow-2xl border border-border animate-scale-in max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-text-primary">Import produktów</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-hover/50 transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* File upload */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Plik CSV
            </label>
            <div className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-accent-primary/50 transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-10 h-10 text-text-muted mx-auto mb-3" />
                <p className="text-sm text-text-secondary">
                  {file ? file.name : 'Kliknij, aby wybrać plik CSV'}
                </p>
                <p className="text-xs text-text-muted mt-1">
                  Kolumny: Nazwa, SKU, Opis, Cena zakupu, Cena sprzedaży, Ilość, Min. ilość
                </p>
              </label>
            </div>
          </div>

          {parseError && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 text-danger text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {parseError}
            </div>
          )}

          {/* Preview */}
          {previewData.length > 0 && (
            <>
              <div className="p-3 rounded-lg bg-accent-primary/10 text-accent-primary text-sm">
                Znaleziono {previewData.length} produktów do importu
              </div>

              <div className="max-h-48 overflow-y-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-surface-hover sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-text-secondary">Nazwa</th>
                      <th className="text-left p-2 text-text-secondary">SKU</th>
                      <th className="text-right p-2 text-text-secondary">Ilość</th>
                      <th className="text-right p-2 text-text-secondary">Cena</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {previewData.slice(0, 10).map((p, i) => (
                      <tr key={i}>
                        <td className="p-2 text-text-primary">{p.name}</td>
                        <td className="p-2 text-text-secondary">{p.sku || '-'}</td>
                        <td className="p-2 text-right text-text-secondary">{p.quantity || 0}</td>
                        <td className="p-2 text-right text-text-secondary">
                          {p.sellingPrice || '-'}
                        </td>
                      </tr>
                    ))}
                    {previewData.length > 10 && (
                      <tr>
                        <td colSpan={4} className="p-2 text-center text-text-muted">
                          ...i {previewData.length - 10} więcej
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <div
                  onClick={() => setUpdateExisting(!updateExisting)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    updateExisting ? 'bg-accent-primary' : 'bg-surface-hover'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      updateExisting ? 'left-7' : 'left-1'
                    }`}
                  />
                </div>
                <span className="text-sm text-text-secondary">
                  Aktualizuj istniejące produkty (według SKU)
                </span>
              </label>
            </>
          )}
        </div>

        <div className="p-6 border-t border-border-subtle flex justify-between gap-3">
          <button
            onClick={handleImport}
            disabled={loading || previewData.length === 0}
            className="btn-primary"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Importuj {previewData.length > 0 && `(${previewData.length})`}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
